import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Flex,
  Text,
  Switch,
  Card as RadixCard,
  Badge,
  Separator,
} from "@radix-ui/themes";
import { AlertTriangle } from "lucide-react";
import SideDrawer from "@/components/ui/SideDrawer";
import { roleAndPermissionService } from "@/services/breeze/RoleAndPermissionService";
import {
  RoleFieldAccessFieldResponse,
  ConfigureRoleFieldAccessRequest,
} from "@/types/breeze/RoleAndPermission";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

interface RoleFieldAccessDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  roleId: number;
  entityId: number;
  entityName: string;
}

const RoleFieldAccessDrawer = ({
  isOpen,
  onClose,
  roleId,
  entityId,
  entityName,
}: RoleFieldAccessDrawerProps) => {
  const queryClient = useQueryClient();
  const [fieldAccess, setFieldAccess] = useState<Record<number, boolean>>({});

  const {
    data: fieldAccessData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["roleFieldAccess", roleId, entityId],
    queryFn: () =>
      roleAndPermissionService.getRoleFieldAccessByEntity(roleId, entityId),
    enabled: isOpen && !!roleId && !!entityId,
  });

  const { mutate: configureFieldAccess, isPending: isConfiguring } =
    useMutation({
      mutationFn: (request: ConfigureRoleFieldAccessRequest) =>
        roleAndPermissionService.configureRoleFieldAccess(roleId, request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["roleFieldAccess", roleId, entityId],
        });
        onClose();
      },
    });

  // Update local state when data loads
  useEffect(() => {
    if (fieldAccessData?.fields) {
      const initialFieldAccess: Record<number, boolean> = {};
      fieldAccessData.fields.forEach((field) => {
        initialFieldAccess[field.field_id] = field.is_active;
      });
      setFieldAccess(initialFieldAccess);
    }
  }, [fieldAccessData]);

  const handleFieldToggle = (fieldId: number, isActive: boolean) => {
    setFieldAccess((prev) => ({
      ...prev,
      [fieldId]: isActive,
    }));
  };

  const handleSave = () => {
    if (!fieldAccessData?.fields) return;

    const request: ConfigureRoleFieldAccessRequest = {
      entity_id: entityId,
      field_access: fieldAccessData.fields.map((field) => ({
        field_id: field.field_id,
        is_active: fieldAccess[field.field_id] ?? field.is_active,
      })),
    };

    configureFieldAccess(request);
  };

  const hasChanges = fieldAccessData?.fields?.some(
    (field) => fieldAccess[field.field_id] !== field.is_active,
  );

  const renderFieldCard = (field: RoleFieldAccessFieldResponse) => {
    const isActive = fieldAccess[field.field_id] ?? field.is_active;
    const displayName = field.field_label || field.field_name;

    return (
      <RadixCard key={field.field_id} size="1">
        <Box p="3">
          <Flex align="center" justify="between">
            <Flex align="center" gap="2" style={{ minWidth: 0, flex: 1 }}>
              <Text size="3" weight="medium" style={{ wordBreak: "break-all" }}>
                {displayName}
              </Text>
              {field.is_system_field && (
                <Badge size="1" color="gray">
                  System
                </Badge>
              )}
            </Flex>
            <Switch
              checked={isActive}
              onCheckedChange={(checked) =>
                handleFieldToggle(field.field_id, checked)
              }
              disabled={field.is_system_field}
            />
          </Flex>
        </Box>
      </RadixCard>
    );
  };

  if (isLoading) {
    return (
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={`Field Access - ${entityName}`}
        width="max-w-lg"
      >
        <LoadingScreen fullPage={false} />
      </SideDrawer>
    );
  }

  if (error) {
    return (
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={`Field Access - ${entityName}`}
        width="max-w-lg"
      >
        <Box p="4">
          <Flex align="center" gap="2">
            <AlertTriangle style={{ width: "16px", height: "16px" }} />
            <Text color="red">Failed to load field access configuration</Text>
          </Flex>
        </Box>
      </SideDrawer>
    );
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Field Access - ${entityName}`}
      width="max-w-lg"
      saveButton={{
        label: "Save Field Access",
        loading: isConfiguring,
        disabled: !hasChanges,
        onClick: handleSave,
      }}
    >
      <Flex direction="column" gap="4">
        <Box>
          <Text size="2" color="gray">
            Configure which fields this role can access for the {entityName}{" "}
            entity. System fields are read-only and cannot be disabled.
          </Text>
        </Box>

        <Separator orientation="horizontal" size="4" />

        <Flex direction="column" gap="2">
          {fieldAccessData?.fields?.map(renderFieldCard)}
        </Flex>

        {fieldAccessData?.fields?.length === 0 && (
          <Box p="6" style={{ textAlign: "center" }}>
            <Text size="2" color="gray">
              No fields found for this entity
            </Text>
          </Box>
        )}
      </Flex>
    </SideDrawer>
  );
};

export default RoleFieldAccessDrawer;
